import type { ApprovalDecisionGuard } from '@/lib/types';

/** Versioned storage shared by every PWA tab on this origin. */
export const APPROVAL_DECISION_GUARDS_STORAGE_KEY =
  'plantme.approvalDecisionGuards.v1';

/** One lock serializes record-level read-modify-write operations across tabs. */
const APPROVAL_DECISION_GUARDS_LOCK = 'plantme.approvalDecisionGuards';
const STORAGE_VERSION = 1;
const MAX_STORED_GUARDS = 100;

interface StoredApprovalDecisionGuards {
  version: typeof STORAGE_VERSION;
  records: ApprovalDecisionGuard[];
}

/** A fail-closed coordination error raised before an approval request is sent. */
export class ApprovalDecisionCoordinationError extends Error {
  readonly code = 'APPROVAL_COORDINATION_UNAVAILABLE';

  constructor() {
    super('This browser cannot safely coordinate approval decisions.');
    this.name = 'ApprovalDecisionCoordinationError';
  }
}

/** Stable identity for one user's decision on one approval. */
export function approvalDecisionGuardKey(
  guard: Pick<ApprovalDecisionGuard, 'orgId' | 'userId' | 'approvalId'>,
): string {
  return `${guard.orgId}\u0000${guard.userId}\u0000${guard.approvalId}`;
}

function isBoundedString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 256;
}

/** Validate untrusted browser storage before it can disable an approval action. */
function parseGuard(value: unknown): ApprovalDecisionGuard | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Partial<ApprovalDecisionGuard>;
  if (
    !isBoundedString(record.orgId) ||
    !isBoundedString(record.userId) ||
    !isBoundedString(record.approvalId) ||
    !isBoundedString(record.transactionId) ||
    (record.action !== 'approve' && record.action !== 'reject') ||
    (record.state !== 'uncertain' && record.state !== 'recorded') ||
    typeof record.dispatchedAt !== 'number' ||
    !Number.isFinite(record.dispatchedAt) ||
    record.dispatchedAt < 0
  ) {
    return null;
  }
  return {
    orgId: record.orgId,
    userId: record.userId,
    approvalId: record.approvalId,
    transactionId: record.transactionId,
    action: record.action,
    state: record.state,
    dispatchedAt: record.dispatchedAt,
  };
}

interface ApprovalDecisionGuardRead {
  ok: boolean;
  records: ApprovalDecisionGuard[];
}

/** Read and validate the complete shared record set without hiding read failure. */
function readStoredGuards(): ApprovalDecisionGuardRead {
  try {
    const raw = localStorage.getItem(APPROVAL_DECISION_GUARDS_STORAGE_KEY);
    if (!raw) return { ok: true, records: [] };
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, records: [] };
    }
    const envelope = parsed as Partial<StoredApprovalDecisionGuards>;
    if (envelope.version !== STORAGE_VERSION || !Array.isArray(envelope.records)) {
      return { ok: false, records: [] };
    }
    if (envelope.records.length > MAX_STORED_GUARDS) {
      return { ok: false, records: [] };
    }

    const byKey = new Map<string, ApprovalDecisionGuard>();
    for (const value of envelope.records) {
      const guard = parseGuard(value);
      if (!guard) return { ok: false, records: [] };
      byKey.set(approvalDecisionGuardKey(guard), guard);
    }
    return { ok: true, records: [...byKey.values()] };
  } catch {
    return { ok: false, records: [] };
  }
}

/** Read, validate, deduplicate, and bound the shared record set. */
export function readApprovalDecisionGuards(): ApprovalDecisionGuard[] {
  return readStoredGuards().records;
}

/** Read the guards belonging to one signed account identity. */
export function approvalDecisionGuardsForOwner(
  orgId: string,
  userId: string,
): Record<string, ApprovalDecisionGuard> {
  const result: Record<string, ApprovalDecisionGuard> = {};
  for (const guard of readApprovalDecisionGuards()) {
    if (guard.orgId === orgId && guard.userId === userId) {
      result[guard.approvalId] = guard;
    }
  }
  return result;
}

/** Freshly read one guard so a peer tab's decision wins over stale in-memory UI. */
export function readApprovalDecisionGuard(
  orgId: string,
  userId: string,
  approvalId: string,
): ApprovalDecisionGuard | null {
  return (
    readApprovalDecisionGuards().find(
      (guard) =>
        guard.orgId === orgId &&
        guard.userId === userId &&
        guard.approvalId === approvalId,
    ) ?? null
  );
}

function writeGuards(records: ApprovalDecisionGuard[]): boolean {
  try {
    const envelope: StoredApprovalDecisionGuards = {
      version: STORAGE_VERSION,
      records,
    };
    localStorage.setItem(APPROVAL_DECISION_GUARDS_STORAGE_KEY, JSON.stringify(envelope));
    return true;
  } catch {
    return false;
  }
}

/**
 * Persist one record using a fresh read. Callers hold the browser-wide lock, so
 * record updates from different tabs cannot erase one another.
 */
export function persistApprovalDecisionGuard(guard: ApprovalDecisionGuard): boolean {
  const stored = readStoredGuards();
  if (!stored.ok) return false;
  const records = stored.records;
  const key = approvalDecisionGuardKey(guard);
  const index = records.findIndex((record) => approvalDecisionGuardKey(record) === key);
  if (index >= 0) {
    records[index] = guard;
  } else {
    if (records.length >= MAX_STORED_GUARDS) return false;
    records.push(guard);
  }
  return writeGuards(records);
}

/** Remove only the exact owner/approval record after authoritative settlement. */
export function deleteApprovalDecisionGuard(
  orgId: string,
  userId: string,
  approvalId: string,
): boolean {
  const stored = readStoredGuards();
  if (!stored.ok) return false;
  const records = stored.records;
  const next = records.filter(
    (guard) =>
      !(
        guard.orgId === orgId &&
        guard.userId === userId &&
        guard.approvalId === approvalId
      ),
  );
  if (next.length === records.length) return true;
  return writeGuards(next);
}

/** Serialize every shared-storage mutation; unsupported browsers fail closed. */
export async function withApprovalDecisionGuardLock<T>(
  run: () => Promise<T>,
): Promise<T> {
  if (typeof navigator === 'undefined' || typeof navigator.locks?.request !== 'function') {
    throw new ApprovalDecisionCoordinationError();
  }
  return navigator.locks.request(
    APPROVAL_DECISION_GUARDS_LOCK,
    { mode: 'exclusive' },
    run,
  );
}
